import { type ButtonHTMLAttributes, type ReactNode } from 'react'

import { Icon, type IconName } from './Icon'
import { cn } from '../lib/cn'

/**
 * Shared button primitive — ported from `FgButton` in `designs/system.jsx`,
 * which M16 derived from node `354:2047` (primary) and `857:959` (destructive).
 *
 * Upstream geometry, MEASURED not invented: fill `green-500`, label `green-50`,
 * padding 24px/10px, radius 16px, Inter Medium 16/24. The app had no button
 * component before this — every button was hand-rolled at its call site, which
 * is why T02's colour swap reached everything and the *shape* swap could not.
 * This is that centre.
 *
 * DERIVED, not from Figma: hover, active, disabled, loading and the focus ring.
 * The Figma file draws no interaction states anywhere (audit gap G31), so every
 * one of them is ours. They are marked at each rule below so a reviewer can
 * tell ported geometry from our own judgement.
 */

export type ButtonKind = 'primary' | 'secondary' | 'ghost' | 'destructive'

/** PORTED — the four fills from `FgButton`. `accent-on` exists for exactly this:
 *  the file specifies `green-50` on the accent fill, not white. */
const KINDS: Record<ButtonKind, string> = {
  primary: 'bg-accent text-accent-on border-accent',
  secondary: 'bg-bg-app text-ink-1 border-line-1',
  ghost: 'bg-transparent text-accent-ink border-transparent',
  destructive: 'bg-sos text-sos-bg border-sos',
}

/** DERIVED — Figma draws no hover state (G31). Each darkens or lifts its own
 *  surface rather than borrowing another kind's colour, so a hovered secondary
 *  never reads as a primary. */
const HOVER: Record<ButtonKind, string> = {
  primary: 'hover:brightness-110',
  secondary: 'hover:bg-bg-soft',
  ghost: 'hover:bg-accent-bg',
  destructive: 'hover:brightness-110',
}

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children'
>

interface ButtonBase extends NativeButtonProps {
  kind?: ButtonKind
  /** Stretches to the container width. `full` in `FgButton`. */
  full?: boolean
  /** Leading icon. Hidden while `loading`, matching the wireframe. */
  icon?: IconName
  /** Shows the spinner, sets `aria-busy` and disables the control. */
  loading?: boolean
  className?: string
}

/**
 * An icon-only button carries no text, so it MUST bring its own accessible
 * name. Expressing that in the type means the common case fails to compile
 * rather than shipping an unlabelled control; the runtime guard below catches
 * the cases types cannot see (a `children` that renders to nothing).
 */
export type ButtonProps = ButtonBase &
  (
    | { children: ReactNode; 'aria-label'?: string }
    | { children?: undefined; 'aria-label': string }
  )

export function Button({
  kind = 'primary',
  full,
  icon,
  loading,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  if (children === undefined && !rest['aria-label']) {
    // Belt and braces with the type union above. An icon-only control with no
    // name is invisible to a screen reader, and on this product that can be an
    // emergency control.
    throw new Error(
      'Button: an icon-only button requires an aria-label. Pass children or aria-label.',
    )
  }

  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        // PORTED — geometry from 354:2047: radius 16 (r3), padding 24/10,
        // label 16/24 at Medium.
        'inline-flex items-center justify-center gap-2 rounded-r3 border px-6 py-2.5 text-[16px] leading-6 font-medium',
        // DERIVED — motion and the focus ring (G31). The ring matches the
        // `.focus-ring` rule in designs/tokens.css: 2px accent at 2px offset.
        //
        // Two things a reader needs to know here. First, `outline-solid` rather
        // than the bare `outline`: tailwind-merge groups the bare utility with
        // outline-COLOR, so `outline-accent` silently swallows it and the ring
        // is left with no style of its own. Every other button in the app
        // writes its focus classes as a raw string that never meets twMerge,
        // which is why this only bites here.
        //
        // Second, these utilities currently RESTATE the ring rather than set
        // it: the `:focus-visible` rule in index.css is unlayered, and
        // unlayered CSS beats Tailwind's `@layer utilities`, so that rule is
        // what actually paints. Verified in the browser. They are kept so the
        // component stays correct if that global rule is ever scoped — but do
        // NOT expect a per-call-site `focus-visible:outline-*` colour to win,
        // because it will not. The system specifies ONE ring colour, accent,
        // for every control including the emergency ones.
        'transition-[filter,background-color,box-shadow,opacity] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        // DERIVED — disabled. Kept greyed AND non-interactive: `disabled` on a
        // native button already removes it from the tab order, which is the
        // half that matters for keyboard users.
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100',
        KINDS[kind],
        !disabled && !loading && HOVER[kind],
        full && 'w-full',
        className,
      )}
    >
      {loading && (
        <span
          // DERIVED — the wireframe uses its shimmer skeleton here; a spinner
          // reads better on a control the user just pressed. Decorative: the
          // state is announced by aria-busy, not by this element.
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
        />
      )}
      {icon && !loading && <Icon name={icon} size={16} />}
      {children}
    </button>
  )
}
